import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import type { AddressType } from '@sweetgreen/garnish';
import {
  AddressTypes,
  JumpNavigationProvider,
  StickyScrollView,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import { AppFooter, LoadingAnimation, NotFoundView } from '@order/components';
import type { DeliveryOrderDetailInput } from '@order/graphql';
import {
  useResetLastInteractedMenuStore,
  useSetLastInteractedMenuStore,
} from '@order/LastInteractedStore';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { BagAccessButton } from '../../components/Bag/subcomponents';
import { useHeaderLeft } from '../../components/Header/Header.hooks';
import type { HeaderIconItems } from '../../components/Header/Header.props';
import type {
  DeliveryMenuScreenProps,
  MenuScreenProps,
} from '../../navigation/AppNavigation.props';
import { useReorderIfRequired } from '../ReorderingScreen';
import { useReorderState } from '../ReorderingScreen/hooks';
import type { RestaurantQuery } from './GraphQL/Restaurant.query.generated';
import {
  useMenuScreenMountTelemetry,
  useRestaurantDisabledCheck,
} from './MenuScreen.hooks';
import { useMenuNavigation } from './MenuScreen.navigation';
import {
  MenuCategories,
  MenuCategoriesNav,
  MenuCollections,
  MenuHero,
} from './subcomponents';

// ────────────────────────────────────────────────────────────────────────────────

export const MenuScreenContent = (props: MenuScreenContentProps) => {
  const { deliveryDetails, restaurantSlug, restaurantData, navigation } = props;

  const {
    addressId,
    location: deliveryLocation,
    deliveryAddress,
    ETA,
  } = deliveryDetails ?? {};
  const locationSelectionType = (
    deliveryLocation
      ? AddressTypes.delivery
      : restaurantData?.isOutpost
      ? AddressTypes.outpost
      : AddressTypes.pickup
  ) as AddressType;

  const { t } = useLocalizationContext();
  const { track } = useTelemetry();

  // ─── Navigation ──────────────────────────────────────────────────

  const { minWidth, currentBreakpoint } = useResponsive();
  const { handleGoToLocations } = useMenuNavigation();
  const { reordering: isReordering } = useReorderState();

  useSetLastInteractedMenuStore({ restaurantSlug, addressId });
  useMenuScreenMountTelemetry(restaurantData);
  useReorderIfRequired(restaurantSlug, addressId);

  // ─── Helpers ───────────────────────────────────────────────────────

  const monitoredGoToLocations = useCallback(() => {
    track('menu.change_location');
    handleGoToLocations(locationSelectionType, addressId);
  }, [addressId, handleGoToLocations, locationSelectionType, track]);

  // ─────────────────────────────────────────────────────────────────

  // Don't display any categories with no products
  const categories = useMemo(() => {
    const allCategories = restaurantData?.menu?.categories ?? [];

    return allCategories.filter(({ products }) => products.length > 0);
  }, [restaurantData?.menu?.categories]);

  const categoriesIds = useMemo(
    () => categories.map((category) => category.id),
    [categories],
  );

  // ─── Navigation options ──────────────────────────────────────────

  const headerLeftItems = useMemo<HeaderIconItems>(
    () => [
      {
        testID: 'nav-bar.change-locations-btn',
        key: 'change-locations-btn',
        icon: 'IconMap',
        accessibilityLabel: t('menu.hero.change-location-label'),
        onPress: monitoredGoToLocations,
      },
    ],
    [monitoredGoToLocations, t],
  );

  const headerLeftXS = useHeaderLeft({ items: headerLeftItems });
  const shouldShowLocationChangeBtn = minWidth.tablet;

  useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: shouldShowLocationChangeBtn ? undefined : headerLeftXS,
    });
  }, [headerLeftXS, navigation, shouldShowLocationChangeBtn]);

  // ─── Styles ──────────────────────────────────────────────────────

  const wrapperStyle = minWidth.tablet ? styles.wrapperWithBorderSM : {};

  // ─── Disabled Restaurant ─────────────────────────────────────────

  const { isRestaurantDisabled, disabledRestaurantNotice } =
    useRestaurantDisabledCheck(restaurantData);

  const resetLastInteractedMenuStore = useResetLastInteractedMenuStore();

  const handleDisabledStorePress = useCallback(() => {
    handleGoToLocations(locationSelectionType);
  }, [locationSelectionType, handleGoToLocations]);

  if (isRestaurantDisabled) {
    return (
      <NotFoundView
        btnLabel={t('not-found-view.select-another-location')}
        description={disabledRestaurantNotice}
        onMount={resetLastInteractedMenuStore}
        onBtnPress={handleDisabledStorePress}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────

  if (isReordering) return <LoadingAnimation />;

  return (
    <>
      <StickyScrollView unstuckStyle={wrapperStyle}>
        {!currentBreakpoint.isMobile && (
          <MenuHero
            deliveryLocation={deliveryLocation}
            deliveryETA={ETA}
            deliveryAddress={deliveryAddress}
            restaurant={restaurantData}
            locationSelectionType={locationSelectionType}
            goToLocations={monitoredGoToLocations}
          />
        )}

        <JumpNavigationProvider jumpTargetIds={categoriesIds}>
          <MenuCategoriesNav
            restaurantSlug={restaurantSlug}
            categories={categories}
          />

          <MenuCollections menuCollectionId={restaurantData?.menu?.id} />

          <MenuCategories
            categories={categories}
            restaurantSlug={restaurantSlug}
            deliveryDetails={deliveryDetails}
          />
        </JumpNavigationProvider>

        <AppFooter />
      </StickyScrollView>

      {currentBreakpoint.isMobile ? <BagAccessButton /> : null}
    </>
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapperWithBorderSM: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.DARK_KALE,
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type MenuScreenContentProps = {
  readonly restaurantSlug: string;
  readonly restaurantData: RestaurantQuery['restaurant'];
  readonly deliveryDetails?: DeliveryOrderDetailInput & {
    readonly addressId: string;
    readonly ETA: string;
    readonly addressName: string;
    readonly location: string;
    readonly deliveryAddress: string;
  };
  readonly navigation:
    | MenuScreenProps['navigation']
    | DeliveryMenuScreenProps['navigation'];
};
